import { Dispatch } from 'react'

import qs from 'qs'

import { getTextFromSelectedDropdownOption } from 'utils/formatters'
import { getUrlParam } from 'utils/mixpanel'
import { makeRequest } from 'utils/requests'
import { fromGlobalId } from 'utils/transformations/graphql'

import { ProgressData } from './UploadFileStandard.types'
import {
  ImportExcelParams,
  ImportOrders,
  ImportRequestParams,
  SheetResult,
} from './UploadFileStandard.types'

type SetStateAction<T> = T | ((prevState: T) => T)

export const uploadOrdersFiles = async (
  files: Array<File>,
  setProgressData: Dispatch<SetStateAction<ProgressData>>,
): Promise<string[] | undefined> => {
  const uploadedFiles: string[] = []

  for (const file of files) {
    try {
      const data = new window.FormData()
      data.append('files[]', file)
      setProgressData({
        progress: (33 / files.length) * (files.indexOf(file) + 1),
        label:
          'Uploading File ' + (files.indexOf(file) + 1) + ' of ' + files.length,
        error: false,
      })
      const response = await makeRequest({
        headers: {
          Accept: 'application/json; text/javascript',
        },
        method: 'post',
        url: '/orders/process_excel_single_file?format=xlsx&order_id=0&newUploader=0',
        data,
      })

      if (response.data && response.data[0]) {
        uploadedFiles.push(response.data[0].name)
      }
    } catch (error) {
      console.error('Error while uploading file:', error)
      // Puedes manejar el error según sea necesario, como registrar, notificar al usuario, etc.
    }
  }
  return uploadedFiles

  // const uploads = files.map((file) => {
  //   const data = new window.FormData()
  //   data.append('files[]', file)

  //   return makeRequest({
  //     headers: {
  //       Accept: 'application/json; text/javascript',
  //       'content-type': 'application/json',
  //     },
  //     method: 'post',
  //     url:
  //       '/orders/process_excel_single_file?format=xlsx&order_id=0&newUploader=0',
  //     data,
  //   })
  // })

  // return Promise.all(uploads).then((result) => {
  //   if (Array.isArray(result)) {
  //     const newFiles: Array<string> = result
  //       .filter((response) => response.data && response.data[0])
  //       .map((response) => response.data[0].name)
  //     return newFiles
  //   }
  // })
}

// @ts-expect-error
export const importOrders: (
  orders: ImportOrders,
) => Promise<string[] | undefined> = ({
  files,
  warehouseOptions,
  connectedAccountsOptions,
  retailerId,
  warehouseId,
  orderTypeOptions,
  orderTypeId,
  poNumber,
  doorId,
  trackEvent,
  location,
}) => {
  const imports = files.map((file) => {
    const params = importRequestParams({
      // @ts-expect-error
      file,
      orderTypeId,
      warehouseId,
      doorId,
      poNumber,
      retailerId,
    })
    const url = `/orders/import_excel_single_file/?${params}`
    return makeRequest({ method: 'get', url, responseType: 'blob' })
  })

  trackExcelOrderModalImportExcel(
    {
      connectedAccountsOptions,
      retailerId,
      warehouseOptions,
      warehouseId,
      orderTypeOptions,
      orderTypeId,
      poNumber,
      location,
    },
    trackEvent,
  )

  return Promise.all(imports).then((responses) => {
    const importedFiles = Promise.all(
      // @ts-expect-error
      responses.map(getImportedFileFromResponse),
    )
    return importedFiles
  })
}

// @ts-expect-error
const importRequestParams: (params: ImportRequestParams) => Promise<File> = ({
  file,
  orderTypeId,
  warehouseId,
  doorId,
  poNumber,
  retailerId,
}) => {
  const params = {
    file_to_import: file,
    order_type: orderTypeId ? fromGlobalId(orderTypeId).id : '',
    retailer_account_id: fromGlobalId(retailerId).id,
    warehouse_name: warehouseId ? fromGlobalId(warehouseId).id : '',
    door_name: doorId ? fromGlobalId(doorId).id : '',
    po_number: poNumber || '',
  }
  return qs.stringify(params)
}

const trackExcelOrderModalImportExcel = (
  config: ImportExcelParams,
  trackEvent: (a: string, b: Object) => void,
) =>
  trackEvent(
    'Orders List_Excel Order Uploaded',
    getExcelOrderModalImportExcelParams(config),
  )

// @ts-expect-error
const getImportedFileFromResponse: (response: {
  data: Blob
  config: { url: string }
}) => Promise<unknown> = (response) => {
  const { data, config } = response
  const filenameMatch = config.url.match(/file_to_import=(.+)&order_type/)
  const filename = (filenameMatch && decodeURIComponent(filenameMatch[1])) || ''

  if (data.type !== 'application/json') {
    return Promise.resolve({
      data,
      filename,
      orders: [],
      error:
        'This order was not imported. Please see the errors column in the results file for more details.',
    })
  }
  return getImportedFileFromBlob(data, filename)
}

const getImportedFileFromBlob: (blob: Blob, filename: string) => void = (
  blob,
  filename,
) => {
  const file: {
    filename: string
    error: string | null
    orders: Array<unknown>
    sheetResults: Array<SheetResult>
  } = { filename, error: null, orders: [], sheetResults: [] }
  const fileReader = new window.FileReader()
  return new Promise((resolve) => {
    fileReader.onload = (event: ProgressEvent<FileReader>) => {
      const body = JSON.parse(String(event?.target?.result))

      if (!body) {
        file.error = 'Incorrect file type. Please use the Excel order template.'
      } else {
        file.error = body.error

        if (body.order_ids) {
          file.orders = body.order_ids.map(
            (order: { ws_order_id: string; reference_number: string }) => ({
              id: order.ws_order_id,
              poNumber: order.reference_number,
            }),
          )
        }

        // sc-247036: per-worksheet outcome (created / error), so the summary
        // step can show exactly which linesheets in this file were imported
        // vs which failed, and why, instead of one generic result for the
        // whole file.
        if (body.sheet_results) {
          file.sheetResults = body.sheet_results.map(
            (sheetResult: {
              sheet_name: string
              status: 'created' | 'error'
              reason?: string
              orders?: Array<{ ws_order_id: string; reference_number: string }>
            }) => ({
              sheetName: sheetResult.sheet_name,
              status: sheetResult.status,
              reason: sheetResult.reason,
              orders: (sheetResult.orders || []).map((order) => ({
                id: order.ws_order_id,
                poNumber: order.reference_number,
              })),
            }),
          )
        }
      }
      resolve(file)
    }
    fileReader.readAsText(blob)
  })
}

const getExcelOrderModalImportExcelParams: (
  params: ImportExcelParams,
) => Object = ({
  connectedAccountsOptions,
  retailerId,
  warehouseOptions,
  warehouseId,
  orderTypeOptions,
  orderTypeId,
  poNumber,
  location,
}) => ({
  'Retailer Search': getTextFromSelectedDropdownOption(
    connectedAccountsOptions,
    retailerId,
  ),
  'Warehouse Name':
    getTextFromSelectedDropdownOption(warehouseOptions, warehouseId) ||
    'No warehouse selected',
  'Order Type':
    getTextFromSelectedDropdownOption(orderTypeOptions, orderTypeId) ||
    'No order type selected',
  'PO Number': poNumber,
  ...getUrlParam(location),
})
